if (!customElements.get('localization-form')) {
  customElements.define('localization-form', class LocalizationForm extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.form = this.querySelector('form');
      this.button = this.querySelector('.localization-form__select');
      this.panel = this.querySelector('.cista-localization__panel');
      this.input = this.querySelector('[data-localization-input]');
      if (!this.form || !this.button || !this.panel || !this.input) return;

      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.search = this.querySelector('[data-country-search]');
      this.reset = this.querySelector('.country-filter__reset-button');
      this.liveRegion = this.querySelector('[data-country-results-count]');
      this.empty = this.querySelector('[data-country-empty]');
      this.list = this.panel.querySelector('.disclosure__list');
      this.items = Array.from(this.panel.querySelectorAll('[data-value]'));
      this.header = this.closest('.header-wrapper');
      this.desktop = window.matchMedia('(min-width: 990px)');

      this.button.addEventListener('click', () => this.openSelector(), options);
      this.panel.querySelector('.cista-localization__close').addEventListener('click', () => this.hidePanel(), options);
      this.panel.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.hidePanel();
      }, options);
      this.panel.addEventListener('close', () => this.afterClose(), options);
      this.panel.addEventListener('click', (event) => {
        if (event.target !== this.panel) return;
        const bounds = this.panel.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) {
          this.hidePanel();
        }
      }, options);
      this.panel.addEventListener('keydown', (event) => this.onKeyDown(event), options);
      this.addEventListener('keyup', (event) => {
        if (event.key === 'Escape' && this.dismissedWithEscape) {
          event.stopPropagation();
          this.dismissedWithEscape = false;
        }
      }, options);
      this.items.forEach((item) => item.addEventListener('click', () => this.selectItem(item), options));
      this.search?.addEventListener('input', () => this.filterCountries(), options);
      this.reset?.addEventListener('click', () => {
        this.search.value = '';
        this.filterCountries();
        this.search.focus();
      }, options);
      window.addEventListener('resize', () => this.positionPanel(), options);
      window.visualViewport?.addEventListener('resize', () => this.positionPanel(), options);
      window.visualViewport?.addEventListener('scroll', () => this.positionPanel(), options);
      window.addEventListener('pageshow', () => {
        this.input.value = this.input.defaultValue;
        this.submitting = false;
        this.panel.removeAttribute('aria-busy');
        this.items.forEach((item) => { item.disabled = false; });
      }, options);
    }

    disconnectedCallback() {
      this.hidePanel(false);
      this.controller?.abort();
      this.controller = null;
    }

    openSelector() {
      if (this.panel.open) {
        this.hidePanel();
        return;
      }
      document.querySelectorAll('localization-form').forEach((selector) => {
        if (selector !== this) selector.hidePanel?.(false);
      });
      this.panel.hidden = false;
      this.button.setAttribute('aria-expanded', 'true');
      document.body.classList.add('cista-localization-open');
      if (this.header && this.hasAttribute('data-prevent-hide')) this.header.preventHide = true;
      this.panel.showModal();
      this.positionPanel();
      const selected = this.items.find((item) => item.getAttribute('aria-current') === 'true');
      const target = this.desktop.matches && this.search ? this.search : selected || this.items[0];
      target?.focus({ preventScroll: true });
      if (selected) selected.scrollIntoView({ block: 'nearest' });
    }

    hidePanel(restoreFocus = true) {
      if (!this.panel?.open) return;
      this.restoreFocus = restoreFocus;
      this.panel.close();
      this.afterClose();
    }

    afterClose() {
      if (!this.button || this.panel.open || this.button.getAttribute('aria-expanded') !== 'true') return;
      this.button.setAttribute('aria-expanded', 'false');
      this.panel.hidden = true;
      if (this.search) {
        this.search.value = '';
        this.filterCountries(false);
      }
      if (this.header && this.hasAttribute('data-prevent-hide')) this.header.preventHide = false;
      if (!document.querySelector('.cista-localization__panel[open]')) document.body.classList.remove('cista-localization-open');
      if (this.restoreFocus !== false && this.isConnected) this.button.focus({ preventScroll: true });
      this.restoreFocus = true;
    }

    positionPanel() {
      if (!this.panel?.open) return;
      const viewport = window.visualViewport;
      const viewportHeight = viewport?.height || window.innerHeight;
      const viewportTop = viewport?.offsetTop || 0;
      this.panel.style.setProperty('--cista-localization-height', `${viewportHeight}px`);
      this.panel.style.setProperty('--cista-localization-bottom', `${Math.max(0, window.innerHeight - viewportHeight - viewportTop)}px`);
      if (!this.desktop.matches) {
        this.panel.style.removeProperty('left');
        this.panel.style.removeProperty('top');
        return;
      }
      const trigger = this.button.getBoundingClientRect();
      const panel = this.panel.getBoundingClientRect();
      const margin = 16;
      const gap = 8;
      const viewportBottom = viewportTop + viewportHeight;
      const spaceBelow = viewportBottom - trigger.bottom - margin - gap;
      const spaceAbove = trigger.top - viewportTop - margin - gap;
      const top = spaceBelow >= panel.height || spaceBelow >= spaceAbove ? trigger.bottom + gap : trigger.top - panel.height - gap;
      const left = this.closest('.footer__localization') ? trigger.left : trigger.right - panel.width;
      this.panel.style.left = `${Math.max(margin, Math.min(left, window.innerWidth - panel.width - margin))}px`;
      this.panel.style.top = `${Math.max(viewportTop + margin, Math.min(top, viewportBottom - panel.height - margin))}px`;
    }

    onKeyDown(event) {
      if (event.key === 'Escape') {
        this.dismissedWithEscape = true;
        event.preventDefault();
        event.stopPropagation();
        this.hidePanel();
        return;
      }
      if (event.key === 'Enter' && event.target === this.search) {
        event.preventDefault();
        return;
      }
      if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
      if (event.target === this.search && ['Home', 'End'].includes(event.key)) return;
      const items = this.items.filter((item) => !item.closest('li').hidden);
      if (!items.length) return;
      event.preventDefault();
      const index = items.indexOf(document.activeElement);
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1 : event.key === 'ArrowDown' ? (index + 1) % items.length : (index < 0 ? items.length - 1 : (index - 1 + items.length) % items.length);
      items[next].focus();
    }

    normalizeString(value) {
      return value.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase().trim();
    }

    filterCountries(announce = true) {
      const query = this.normalizeString(this.search.value);
      let count = 0;
      this.items.forEach((item) => {
        const visible = this.normalizeString(item.dataset.search).includes(query);
        item.closest('li').hidden = !visible;
        if (visible) count += 1;
      });
      this.reset.hidden = !query;
      this.empty.hidden = count > 0;
      const message = window.accessibilityStrings?.countrySelectorSearchCount;
      this.liveRegion.textContent = announce && message ? message.replace('[count]', count) : '';
      this.list.scrollTop = 0;
    }

    selectItem(item) {
      if (this.submitting) return;
      if (this.input.name === 'language_code') window.CistaLanguagePreference?.remember(item.dataset.value);
      if (item.dataset.value === this.input.value) {
        this.hidePanel();
        return;
      }
      this.input.value = item.dataset.value;
      const returnTo = this.form.querySelector('input[name="return_to"]');
      if (returnTo) returnTo.value = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      this.submitting = true;
      this.panel.setAttribute('aria-busy', 'true');
      this.items.forEach((option) => { option.disabled = true; });
      this.form.requestSubmit();
    }
  });
}
